import React, { useEffect, useState } from 'react'
import translate from '../../../core/translate'
import Content from 'blocks.app/content/content'
import { Search } from './_search/Search'
import { cn } from 'ethcss'
import styles from './advertisementIds.jcss'
import { Header } from './__header/Header'
import InfiniteScroll from 'react-infinite-scroller'
import LoaderLazy from '../../../blocks.app/loader/_lazy/loader__lazy'
import { api } from '../../../core/api/ConnectionManager'
import Checkbox from '../../../blocks.simple/checkbox/checkbox'
import Icon from '../../../blocks.simple/icon/icon'
import { AdvertisementIdAdd } from './_add/AdvertisementIdAdd'
import Button from '../../../blocks.simple/button/button'

export type AdvertisementId = {
    id: number
    name: string
}

export const AdvertisementIds = ({
    selectOnly,
    onSelect,
    selectedIds,
    filter,
}: {
    selectOnly?: boolean
    onSelect?: (ids: number[]) => void
    selectedIds: number[]
    filter?: {
        digitalSignageId?: number[]
        groupId?: number[]
    }
}) => {
    const [advertisementIds, setAdvertisementIds] = useState<AdvertisementId[]>([])
    const [selectedItems, setSelectedItems] = useState<number[]>([])
    const [advertisementIdData, setAdvertisementIdData] = useState<any | null>(null)
    const [search, setSearch] = useState<string>('')
    const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null)
    const [multipleDeleteConfirm, setMultipleDeleteConfirm] = useState<boolean>(false)
    const [nextPageTokenRes, setNextPageTokenRes] = useState<string>('')
    const [isInitialized, setIsInitialized] = useState<boolean>(false)

    const getAdvertisementIdList = (query: string, nextPageToken: string | null) => {
        const data: any = nextPageToken
            ? {
                  nextPageToken: nextPageToken,
              }
            : {
                  limit: 30,
              }

        if (query) {
            data.query = query
        }
        if (filter && !nextPageTokenRes) {
            data.filter = filter
        }

        api.send('getAdvertisementIds', data).then((res: any) => {
            if (!res && !res.data) return

            setNextPageTokenRes(res.nextPageToken)

            if (nextPageToken) {
                setAdvertisementIds([...advertisementIds, ...res.data])
            } else {
                setAdvertisementIds(res.data)
            }
        })
    }
    const onSelectItem = (id: number) => {
        if (selectedItems.includes(id)) {
            setSelectedItems(selectedItems.filter((item) => item !== id))
            return
        }

        setSelectedItems([id, ...selectedItems])
    }
    const onDeleteItem = (id: number) => {
        api.send('deleteAdvertisementId', { id: [id] }).then(() => setDeleteConfirm(null))
    }
    const deleteAllSelected = () => {
        api.send('deleteAdvertisementId', { id: selectedItems }).then(() => setMultipleDeleteConfirm(false))
    }
    const onCancelDelete = () => {
        setDeleteConfirm(null)
        setMultipleDeleteConfirm(false)
    }
    const onDeleteConfirm = (id: number) => {
        setDeleteConfirm(id)
    }
    const onMultipleDeleteConfirm = () => {
        setMultipleDeleteConfirm(true)
    }
    const selectAll = () => {
        setSelectedItems(advertisementIds.map((advertisementId) => advertisementId.id))
    }
    const clearSelection = () => {
        setSelectedItems([])
    }
    const dropdownSelect = (select: { id: string }) => {
        if (select.id === 'selectAll') {
            selectAll()
            return
        }

        clearSelection()
    }

    const onAddAdvertisementId = () => {
        setAdvertisementIdData({
            name: '',
        })
    }
    const onEditAdvertisementId = (advertisementId: AdvertisementId) => {
        setAdvertisementIdData(advertisementId)
    }
    const onAdvertisementIdChange = (field: string, value: string) => {
        if (advertisementIdData) {
            setAdvertisementIdData({
                ...advertisementIdData,
                [field]: value,
            })
        }
    }
    const onCloseModal = () => {
        setAdvertisementIdData(null)
    }
    const onSaveAdvertisementId = () => {
        if (!advertisementIdData) return

        const data: {
            id?: number
            name: string
        } = {
            name: advertisementIdData.name,
        }

        if (advertisementIdData.id) {
            data.id = advertisementIdData.id

            api.send('updateAdvertisementId', data).then(onCloseModal)

            return
        }

        api.send('createAdvertisementId', data).then(onCloseModal)
    }

    const onAdvertisementIdCreated = (advertisementId: AdvertisementId) => {
        setAdvertisementIds([advertisementId, ...advertisementIds])
    }
    const onAdvertisementIdUpdated = (advertisementId: AdvertisementId) => {
        setAdvertisementIds(
            advertisementIds.map((el) => {
                if (el.id === advertisementId.id) {
                    return advertisementId
                }

                return el
            })
        )
    }
    const onAdvertisementIdDeleted = (res: any) => {
        if (!res) return

        setAdvertisementIds(
            advertisementIds.filter((advertisementId) => !res.id.find((id: number) => id === advertisementId.id))
        )
    }

    useEffect(() => {
        if (selectedIds && selectedIds.length && !isInitialized) {
            setSelectedItems(selectedIds)
            setIsInitialized(true)
        }
    }, [selectedIds])

    useEffect(() => {
        getAdvertisementIdList(search, null)
    }, [search])

    useEffect(() => {
        const listenersId: string[] = []

        api.addObserver('advertisementIdCreated', onAdvertisementIdCreated, listenersId)
        api.addObserver('advertisementIdUpdated', onAdvertisementIdUpdated, listenersId)
        api.addObserver('advertisementIdDeleted', onAdvertisementIdDeleted, listenersId)

        return () => listenersId.forEach((id) => api.removeObserver(id))
    }, [advertisementIds])

    useEffect(() => {
        if (onSelect) {
            onSelect(selectedItems)
        }
    }, [selectedItems])

    const renderContent = () => {
        return (
            <div>
                {!selectOnly && <Search search={search} setSearch={setSearch} dropdownSelect={dropdownSelect} />}
                <div className={cn(styles.wrapper, styles.scrollWrapper)}>
                    <Header
                        selectedItemsLength={selectedItems.length}
                        deleteAllSelected={onMultipleDeleteConfirm}
                        onAdd={onAddAdvertisementId}
                        selectOnly={selectOnly}
                        selected={selectedItems.length > 0}
                        clearSelection={clearSelection}
                        selectAll={selectAll}
                    />
                    {advertisementIds.length ? (
                        <div className={styles.tableDisplay}>
                            <div className={cn(styles.adId, styles.adIdHeader, styles.widthFull)}>
                                <div className={styles.checkbox}>
                                    <span className={styles.checkboxHeader}>{translate('choice')}</span>
                                </div>
                                <div>{translate('id')}</div>
                            </div>
                            <InfiniteScroll
                                className={styles.widthFull}
                                loadMore={() => getAdvertisementIdList(search, nextPageTokenRes)}
                                hasMore={nextPageTokenRes !== null}
                                initialLoad={false}
                                loader={<LoaderLazy key="iptv_loader" active />}
                                threshold={10}
                            >
                                <div>
                                    {advertisementIds.map((item) => {
                                        return (
                                            <div className={styles.adId} key={item.id}>
                                                <div className={styles.checkbox}>
                                                    <Checkbox
                                                        checked={selectedItems.includes(item.id)}
                                                        onClick={() => onSelectItem(item.id)}
                                                    />
                                                </div>
                                                <div>{item.name}</div>
                                                {!selectOnly && (
                                                    <div className={styles.changeButtons}>
                                                        <div>
                                                            <Icon
                                                                type={'pencil'}
                                                                onClick={() => onEditAdvertisementId(item)}
                                                            />
                                                        </div>
                                                        <div>
                                                            <Icon
                                                                type={'delete'}
                                                                onClick={() => onDeleteConfirm(item.id)}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            </InfiniteScroll>
                        </div>
                    ) : (
                        <div className={styles.emptyList}>
                            <Icon type={'not_allow'} size={60} className={styles.notFoundIcon} />
                            {translate('noIds')}
                        </div>
                    )}
                </div>
                <AdvertisementIdAdd
                    advertisementId={advertisementIdData}
                    onChange={onAdvertisementIdChange}
                    onClose={onCloseModal}
                    onSave={onSaveAdvertisementId}
                />
                {(deleteConfirm || multipleDeleteConfirm) && (
                    <div className={styles.deleteConfirmWrapper}>
                        <div className={styles.deleteConfirm}>
                            <div>
                                {translate(
                                    deleteConfirm ? 'deleteAdvertisementIdConfirm' : 'deleteAdvertisementIdsConfirm'
                                )}
                            </div>
                            <div className={styles.controls}>
                                <Button mod={'withBorder'} className={styles.button} onClick={onCancelDelete}>
                                    {translate('cancel')}
                                </Button>
                                <Button
                                    mod={'fill'}
                                    className={styles.button}
                                    onClick={() => (deleteConfirm ? onDeleteItem(deleteConfirm) : deleteAllSelected())}
                                >
                                    {translate('delete')}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    return !selectOnly ? <Content title={translate('advertisementIds')}>{renderContent()}</Content> : renderContent()
}
